import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { ArrowUpRight, Star, Search, FolderOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProjectVisual } from '../components/projectVisual';
import Modal from '../components/Modal';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalConfig, setModalConfig] = useState<any>({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    fetchProjects();
  }, [search]);

  const fetchProjects = async () => {
    try {
      const { data } = await api.get(`/projects?q=${search}`);
      setProjects(data);
    } catch (err) { console.error("Erro projetos"); }
    finally { setLoading(false); }
  };

  const toggleFavorite = async (projectId: string) => {
    try {
      await api.patch(`/projects/${projectId}/favorite`);
      fetchProjects();
    } catch (err) { setModalConfig({ isOpen: true, title: 'Erro', message: 'Não foi possível atualizar o favorito.', type: 'danger' }); }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      <Modal {...modalConfig} onClose={() => setModalConfig({ ...modalConfig, isOpen: false })} cancelText="Fechar" />
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Dashboard</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {loading ? 'A carregar projetos…' : `${projects.length} ${projects.length === 1 ? 'projeto' : 'projetos'}`}
          </p>
        </div>

        <div className="relative w-full md:w-[360px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            placeholder="Pesquisar projetos…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 transition-all text-slate-700 dark:text-slate-200 font-medium placeholder:text-slate-400 placeholder:font-normal"
          />
        </div>
      </div>

      {projects.length === 0 && !loading ? (
        <div className="py-24 text-center bg-slate-50 dark:bg-slate-800/40 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
          <FolderOpen className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={40} />
          <p className="text-slate-500 dark:text-slate-400 font-medium">{search ? 'Nenhum projeto corresponde à pesquisa.' : 'Ainda não há projetos.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map(project => (
            <div
              key={project.id}
              onClick={() => navigate(`/project/${project.id}`)}
              className="group bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 hover:border-blue-400/60 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-none hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col"
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`w-12 h-12 rounded-2xl overflow-hidden flex items-center justify-center transition-all ${project.is_favorite ? 'bg-amber-500 text-white' : 'bg-blue-600 text-white dark:bg-blue-900/40'}`}>
                  <ProjectVisual image={project.image} size={24} />
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(project.id); }}
                  className={`p-2 -mr-1 -mt-1 rounded-lg transition-all ${project.is_favorite ? 'text-amber-500' : 'text-slate-300 dark:text-slate-600 hover:text-amber-500'}`}
                  title={project.is_favorite ? 'Remover dos favoritos' : 'Marcar como favorito'}
                >
                  <Star size={18} fill={project.is_favorite ? 'currentColor' : 'none'} />
                </button>
              </div>

              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1.5 tracking-tight truncate" title={project.name}>{project.name}</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-2 leading-relaxed flex-1">
                {project.description || 'Sem descrição.'}
              </p>

              <div className="pt-5 mt-5 border-t border-slate-100 dark:border-slate-700/60 flex justify-between items-center">
                <span className="text-xs text-slate-400">
                  {new Date(project.created_at).toLocaleDateString('pt-PT', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-400 group-hover:text-blue-600 transition-colors">
                  Abrir <ArrowUpRight size={16} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
